import {
  type BoardCardMove,
  type BoardLabels,
  type CellAddress,
  type ItemId,
  useBoardCardMoves,
  useBoardLabels,
} from '@github-ui/board-core'
import {
  ArrowBothIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  KebabHorizontalIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, type IconButtonProps} from '@primer/react'
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'

import styles from './BoardCardFrame.module.css'

type BoardCardMenuProps = {
  itemId: ItemId
  address: CellAddress
  label: string
  actions?: ReactNode
  trailingActions?: ReactNode
  onIntent?: () => void
  /** Whether the card holds the board's one tab stop, which the button shares. */
  isTabStop: boolean
  reportDialogOpen: (isOpen: boolean) => void
}

/**
 * The button in a card's top corner and the menu it opens: whatever the caller added, then
 * a "Move to" submenu with the moves the board offers for the card, then the caller's trailing items.
 *
 * The menu items mount only while it is open, so a closed menu reads none of the board's
 * data. Each closed menu still mounts Primer's anchored overlay and the button tooltip.
 */
export function BoardCardMenu({
  itemId,
  address,
  label,
  actions,
  trailingActions,
  onIntent,
  isTabStop,
  reportDialogOpen,
}: BoardCardMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isRightToLeft, setIsRightToLeft] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const isOpenRef = useRef(false)

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Read when the menu opens rather than during render, from the element, because a
      // board can sit inside a subtree that states its own direction.
      const anchor = anchorRef.current
      if (open && anchor !== null) {
        setIsRightToLeft(anchor.ownerDocument.defaultView?.getComputedStyle(anchor).direction === 'rtl')
      }

      isOpenRef.current = open
      setIsOpen(open)
      reportDialogOpen(open)
    },
    [reportDialogOpen],
  )

  // `useBoardCard` reports a card taken off the board with its menu open. This covers the
  // menu going away while the card stays, such as a board that stops moving cards.
  useEffect(
    () => () => {
      if (isOpenRef.current) reportDialogOpen(false)
    },
    [reportDialogOpen],
  )

  return (
    <div className={styles.CardMenu}>
      <ActionMenu open={isOpen} onOpenChange={onOpenChange} anchorRef={anchorRef}>
        <ActionMenu.Anchor>
          <CardMenuButton
            icon={KebabHorizontalIcon}
            aria-label={label}
            variant="invisible"
            size="small"
            isTabStop={isTabStop}
            onPointerEnter={onIntent}
            onFocus={onIntent}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList>
            {actions}
            <BoardCardMoveItems
              itemId={itemId}
              address={address}
              isRightToLeft={isRightToLeft}
              hasActions={hasContent(actions)}
            />
            {trailingActions}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

/**
 * The menu button, reachable by `Tab` only from the card holding the board's tab stop. One
 * stop per card would make a board of a hundred cards a hundred stops longer.
 *
 * `ActionMenu.Anchor` spreads `tabIndex: 0` over its child, so this sets the value after
 * that spread. Passed to `IconButton` directly, it is overwritten.
 */
function CardMenuButton({isTabStop, ...props}: IconButtonProps & {isTabStop: boolean}) {
  return <IconButton {...props} tabIndex={isTabStop ? 0 : -1} />
}

function BoardCardMoveItems({
  itemId,
  address,
  isRightToLeft,
  hasActions,
}: {
  itemId: ItemId
  address: CellAddress
  isRightToLeft: boolean
  hasActions: boolean
}) {
  const labels = useBoardLabels()
  const {moves, refusal, moveCard} = useBoardCardMoves(itemId, address)
  if (moves.length === 0) return null

  const divider = hasActions ? <ActionList.Divider /> : null

  if (refusal !== undefined) {
    return (
      <>
        {divider}
        {/* One item carrying the caller's reason, rather than a submenu of moves that repeat it. */}
        <ActionList.Item inactiveText={refusal}>
          <ActionList.LeadingVisual>
            <ArrowBothIcon />
          </ActionList.LeadingVisual>
          {labels.moveCard}
        </ActionList.Item>
      </>
    )
  }

  return (
    <>
      {divider}
      <ActionMenu>
        <ActionMenu.Anchor>
          <ActionList.Item>
            <ActionList.LeadingVisual>
              <ArrowBothIcon />
            </ActionList.LeadingVisual>
            {labels.moveCard}
          </ActionList.Item>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            {moves.map(move => {
              const {name, Icon} = directionOf(move, labels, isRightToLeft)
              const destination = destinationOf(move)

              return (
                <ActionList.Item
                  key={`${move.axis}:${move.step}`}
                  disabled={!move.isAvailable}
                  onSelect={() => moveCard(move)}
                >
                  <ActionList.LeadingVisual>
                    <Icon />
                  </ActionList.LeadingVisual>
                  {name}
                  {destination === undefined ? null : (
                    <ActionList.Description variant="inline">{destination}</ActionList.Description>
                  )}
                </ActionList.Item>
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

type Direction = {name: string; Icon: typeof ArrowUpIcon}

/** The column axis follows the writing direction, so its previous column is on the right in a right-to-left board. */
function directionOf({axis, step}: BoardCardMove, labels: BoardLabels, isRightToLeft: boolean): Direction {
  if (axis === 'card') {
    return step === 'previous'
      ? {name: labels.moveCardUp, Icon: ArrowUpIcon}
      : {name: labels.moveCardDown, Icon: ArrowDownIcon}
  }

  const isLeft = (step === 'previous') !== isRightToLeft

  return isLeft ? {name: labels.moveCardLeft, Icon: ArrowLeftIcon} : {name: labels.moveCardRight, Icon: ArrowRightIcon}
}

/** The part of the destination that changes: the column across the board, the swimlane along it. */
function destinationOf({axis, destination}: BoardCardMove): string | undefined {
  if (destination === undefined) return undefined

  return axis === 'column' ? destination.columnLabel : destination.rowLabel
}

/**
 * Whether a caller's actions draw anything. `null`, `undefined`, booleans and the empty
 * string are what a conditional in JSX leaves behind, and none of them is an item.
 */
export function hasContent(node: ReactNode): boolean {
  if (Array.isArray(node)) return node.some(hasContent)

  return node !== null && node !== undefined && typeof node !== 'boolean' && node !== ''
}
